// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InvoiceAgingSummary } from '@/billing/api/get-invoice-aging-summary';
import { InvoiceListItem } from '@/billing/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';

// Aging bucket codes (URL form with ASCII hyphens). Mirrors the backend's
// aging_bucket values. The table headers/chips use en-dash display labels
// ('1–30', '31–60', '61–90') — we map code → label at the transform
// boundary and keep the display labels on `InvoiceRow.bucket` so the
// chip/color helpers, which switch on the label string, continue to work.
export const BUCKET_KEY_VALUES = [
  'current',
  '1-30',
  '31-60',
  '61-90',
  '91+',
] satisfies [string, ...string[]];
export type BucketKey = (typeof BUCKET_KEY_VALUES)[number];

// Map API bucket codes → display labels. The invoice row helpers
// (`bucketChipProps`, `invoiceBalanceColor`) switch on the label string,
// so we convert at the transform boundary and keep the label on each row.
const CODE_TO_DISPLAY_BUCKET: Record<BucketKey, string> = {
  current: 'Current',
  '1-30': '1–30',
  '31-60': '31–60',
  '61-90': '61–90',
  '91+': '91+',
};

// Map display labels back to URL-safe keys (only used for bucket filtering
// inside the aggregated data — the filter toggle already speaks URL codes).
const DISPLAY_BUCKET_TO_KEY: Record<string, BucketKey> = {
  Current: 'current',
  '1–30': '1-30',
  '31–60': '31-60',
  '61–90': '61-90',
  '91+': '91+',
};

// Display label used when an invoice has no project. Rendered as the group
// label in "Group by: Project" mode, the project column in invoice-detail
// rows, and the "Not set" sentinel value on the Project filter dropdown —
// kept consistent so the three surfaces read as one concept.
const NO_PROJECT_LABEL = 'Not set';

export type GroupByDimension = 'company' | 'project';

// Each invoice row carries both its project and customer name so the
// group-by transform never has to rewrite the object to swap dimensions.
// projectKey is carried through for client-side filtering; isoCode keeps
// each row's currency so money cells render in their own denomination.
export interface InvoiceRow {
  key: string;
  invoiceNumber: string;
  project: string;
  projectKey: string | null;
  company: string;
  isoCode: IsoCurrencyCode;
  invoiceDate: string;
  dueDate: string;
  total: Decimal;
  balance: Decimal;
  daysOverdue: number;
  bucket: string;
}

export interface CompanyRow {
  company: string;
  isoCode: IsoCurrencyCode;
  current: Decimal;
  d1_30: Decimal;
  d31_60: Decimal;
  d61_90: Decimal;
  d91: Decimal;
  invoices: InvoiceRow[];
}

export interface GroupedRow {
  label: string;
  isoCode: IsoCurrencyCode;
  current: Decimal;
  d1_30: Decimal;
  d31_60: Decimal;
  d61_90: Decimal;
  d91: Decimal;
  invoices: InvoiceRow[];
  /** The alternate dimension shown in expanded detail rows */
  detailDimensionLabel: string;
}

interface AgingBuckets {
  current: Decimal;
  d1_30: Decimal;
  d31_60: Decimal;
  d61_90: Decimal;
  d91: Decimal;
}

interface BucketCard {
  label: string;
  amount: Decimal;
  count: number;
  colorKey: string;
}

// One labeled section of 5 bucket cards for a single currency.
export interface CurrencyBucketCards {
  isoCode: IsoCurrencyCode;
  cards: BucketCard[];
}

interface AgingFooterTotal {
  isoCode: IsoCurrencyCode;
  current: Decimal;
  d1_30: Decimal;
  d31_60: Decimal;
  d61_90: Decimal;
  d91: Decimal;
  total: Decimal;
}

function parseAgingAmount(amount: string): Decimal {
  return parseCurrencyAmount(amount) ?? new CurrencyDecimal(0);
}

function formatAgingDate(dt: DateTime): string {
  return dt.toLocaleString({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Format a bucket/balance amount in a group's own currency. The pipeline
// sums balances as Decimals within a single-currency group, so the display
// layer re-pairs the amount with the group's ISO code.
export function formatAgingAmount(
  value: Decimal,
  currencyCode: IsoCurrencyCode
): string {
  return formatCurrencyValue({ amount: value, isoCode: currencyCode });
}

/** Sums a row's five aging buckets. */
export function sumAgingBuckets(row: AgingBuckets): Decimal {
  return row.current
    .plus(row.d1_30)
    .plus(row.d31_60)
    .plus(row.d61_90)
    .plus(row.d91);
}

// Groups the flat invoice list into per-(company, currency) rows with bucket
// subtotals. Each group is single-currency: keying on (company, isoCode)
// keeps a company's invoices in different currencies as separate rows so the
// summed bucket totals never mix denominations.
export function buildCompanyRows(invoices: InvoiceListItem[]): CompanyRow[] {
  const byCompany = new Map<string, CompanyRow>();

  for (const inv of invoices) {
    // Invoices without a customer (rare edge case — FE listing allows null
    // customerName) are grouped under an explicit placeholder so the row
    // still surfaces rather than collapsing into whichever group hit the
    // map first.
    const company = inv.customerName ?? '(No customer)';
    const currencyCode = inv.currency;
    const bucketLabel = CODE_TO_DISPLAY_BUCKET[inv.agingBucket];
    const balance = inv.balance.amount;
    const total = inv.total.amount;

    const mapKey = `${company}|${currencyCode}`;
    let row = byCompany.get(mapKey);
    if (!row) {
      row = {
        company,
        isoCode: currencyCode,
        current: new CurrencyDecimal(0),
        d1_30: new CurrencyDecimal(0),
        d31_60: new CurrencyDecimal(0),
        d61_90: new CurrencyDecimal(0),
        d91: new CurrencyDecimal(0),
        invoices: [],
      };
      byCompany.set(mapKey, row);
    }

    switch (inv.agingBucket) {
      case 'current':
        row.current = row.current.plus(balance);
        break;
      case '1-30':
        row.d1_30 = row.d1_30.plus(balance);
        break;
      case '31-60':
        row.d31_60 = row.d31_60.plus(balance);
        break;
      case '61-90':
        row.d61_90 = row.d61_90.plus(balance);
        break;
      case '91+':
        row.d91 = row.d91.plus(balance);
        break;
    }

    row.invoices.push({
      key: inv.key,
      invoiceNumber: inv.invoiceNumber,
      project: inv.projectName ?? NO_PROJECT_LABEL,
      projectKey: inv.projectKey,
      company,
      isoCode: currencyCode,
      invoiceDate: formatAgingDate(inv.invoiceDate),
      dueDate: inv.dueDate ? formatAgingDate(inv.dueDate) : '',
      total,
      balance,
      daysOverdue: inv.daysOverdue,
      bucket: bucketLabel,
    });
  }

  return Array.from(byCompany.values()).sort((a, b) => {
    const comparison = sumAgingBuckets(b).comparedTo(sumAgingBuckets(a));
    if (comparison !== 0) {
      return comparison;
    }

    return a.company.localeCompare(b.company);
  });
}

/** Filter company data to only include invoices in the selected bucket, recomputing summary values */
export function filterDataByBucket(
  data: CompanyRow[],
  bucketFilter: BucketKey | undefined
): CompanyRow[] {
  if (bucketFilter === undefined) return data;

  return data
    .map((company) => {
      const filtered = company.invoices.filter((inv) => {
        const key = DISPLAY_BUCKET_TO_KEY[inv.bucket];
        return key === bucketFilter;
      });
      if (filtered.length === 0) return null;

      let current = new CurrencyDecimal(0);
      let d1_30 = new CurrencyDecimal(0);
      let d31_60 = new CurrencyDecimal(0);
      let d61_90 = new CurrencyDecimal(0);
      let d91 = new CurrencyDecimal(0);
      for (const inv of filtered) {
        const key = DISPLAY_BUCKET_TO_KEY[inv.bucket];
        if (key === 'current') current = current.plus(inv.balance);
        else if (key === '1-30') d1_30 = d1_30.plus(inv.balance);
        else if (key === '31-60') d31_60 = d31_60.plus(inv.balance);
        else if (key === '61-90') d61_90 = d61_90.plus(inv.balance);
        else if (key === '91+') d91 = d91.plus(inv.balance);
      }

      return {
        ...company,
        current,
        d1_30,
        d31_60,
        d61_90,
        d91,
        invoices: filtered,
      };
    })
    .filter((row): row is CompanyRow => row !== null);
}

// Narrows the company/invoice set to rows that match a free-text query.
// Matches are case-insensitive against the company name, invoice number,
// or project name. A hit on the company name surfaces all of that
// company's invoices (the user searched for the group); a hit on
// invoice number or project narrows the group to matching invoices only.
// Bucket subtotals are recomputed from the narrowed set so the summary
// columns stay in sync with the visible rows. Empty query short-circuits.
export function filterDataBySearch(
  data: CompanyRow[],
  query: string
): CompanyRow[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return data;

  return data
    .map((company) => {
      const companyMatches = company.company.toLowerCase().includes(q);
      const filtered = companyMatches
        ? company.invoices
        : company.invoices.filter(
            (inv) =>
              inv.invoiceNumber.toLowerCase().includes(q) ||
              inv.project.toLowerCase().includes(q)
          );
      if (filtered.length === 0) return null;

      let current = new CurrencyDecimal(0);
      let d1_30 = new CurrencyDecimal(0);
      let d31_60 = new CurrencyDecimal(0);
      let d61_90 = new CurrencyDecimal(0);
      let d91 = new CurrencyDecimal(0);
      for (const inv of filtered) {
        const key = DISPLAY_BUCKET_TO_KEY[inv.bucket];
        if (key === 'current') current = current.plus(inv.balance);
        else if (key === '1-30') d1_30 = d1_30.plus(inv.balance);
        else if (key === '31-60') d31_60 = d31_60.plus(inv.balance);
        else if (key === '61-90') d61_90 = d61_90.plus(inv.balance);
        else if (key === '91+') d91 = d91.plus(inv.balance);
      }

      return {
        ...company,
        current,
        d1_30,
        d31_60,
        d61_90,
        d91,
        invoices: filtered,
      };
    })
    .filter((row): row is CompanyRow => row !== null);
}

// Regroups the per-(company, currency) rows by the chosen dimension.
// Company mode is a straight projection (companyRows are already one row
// per company+currency). Project mode folds the ISO code into the group
// key so a project billed in two currencies splits into one single-currency
// group per currency, matching the export's per-(dimension, currency)
// grouping.
export function groupDataByDimension(
  companyRows: CompanyRow[],
  groupBy: GroupByDimension
): GroupedRow[] {
  if (groupBy === 'company') {
    return companyRows.map((row) => ({
      label: row.company,
      isoCode: row.isoCode,
      current: row.current,
      d1_30: row.d1_30,
      d31_60: row.d31_60,
      d61_90: row.d61_90,
      d91: row.d91,
      invoices: row.invoices,
      detailDimensionLabel: 'Project',
    }));
  }

  // Projects are keyed on their nano ID (reference-identity) rather than
  // display name so two projects that share a name stay distinct.
  // Projectless invoices compose a per-customer synthetic key so
  // customer A's "Not set" doesn't merge with customer B's. The ISO code is
  // folded into the key so a project's two currencies stay separate.
  const byProject = new Map<string, GroupedRow>();
  for (const companyRow of companyRows) {
    for (const inv of companyRow.invoices) {
      const dimensionKey =
        inv.projectKey !== null
          ? `proj:${inv.projectKey}`
          : `noproj:${inv.company}`;
      const mapKey = `${dimensionKey}|${inv.isoCode}`;
      const label =
        inv.projectKey !== null
          ? inv.project
          : `${NO_PROJECT_LABEL} — ${inv.company}`;
      let entry = byProject.get(mapKey);
      if (!entry) {
        entry = {
          label,
          isoCode: inv.isoCode,
          current: new CurrencyDecimal(0),
          d1_30: new CurrencyDecimal(0),
          d31_60: new CurrencyDecimal(0),
          d61_90: new CurrencyDecimal(0),
          d91: new CurrencyDecimal(0),
          invoices: [],
          detailDimensionLabel: 'Company',
        };
        byProject.set(mapKey, entry);
      }
      entry.invoices.push(inv);
      const key = DISPLAY_BUCKET_TO_KEY[inv.bucket];
      if (key === 'current') entry.current = entry.current.plus(inv.balance);
      else if (key === '1-30') entry.d1_30 = entry.d1_30.plus(inv.balance);
      else if (key === '31-60') entry.d31_60 = entry.d31_60.plus(inv.balance);
      else if (key === '61-90') entry.d61_90 = entry.d61_90.plus(inv.balance);
      else if (key === '91+') entry.d91 = entry.d91.plus(inv.balance);
    }
  }

  return Array.from(byProject.values()).sort((a, b) =>
    sumAgingBuckets(b).comparedTo(sumAgingBuckets(a))
  );
}

// Builds the per-currency summary-card sections from the aging summary's
// byCurrency buckets. One section per currency, each with the fixed
// 5-bucket card sequence. Amounts are parsed to Decimals and re-paired with
// the section's ISO code at render time.
export function buildCurrencyBucketCards(
  summary: InvoiceAgingSummary
): CurrencyBucketCards[] {
  return summary.byCurrency.map((bucket) => ({
    isoCode: bucket.isoCode,
    cards: [
      {
        label: 'Current',
        amount: parseAgingAmount(bucket.current.amount),
        count: bucket.current.count,
        colorKey: 'current',
      },
      {
        label: '1–30 days',
        amount: parseAgingAmount(bucket['1-30'].amount),
        count: bucket['1-30'].count,
        colorKey: 'd1_30',
      },
      {
        label: '31–60 days',
        amount: parseAgingAmount(bucket['31-60'].amount),
        count: bucket['31-60'].count,
        colorKey: 'd31_60',
      },
      {
        label: '61–90 days',
        amount: parseAgingAmount(bucket['61-90'].amount),
        count: bucket['61-90'].count,
        colorKey: 'd61_90',
      },
      {
        label: '91+ days',
        amount: parseAgingAmount(bucket['91+'].amount),
        count: bucket['91+'].count,
        colorKey: 'd91',
      },
    ],
  }));
}

// Footer totals, summed per currency so different denominations are never
// added together. Each grouped row is single-currency, so this folds rows
// into one total row per ISO code, ordered by first appearance.
export function computeAgingFooterTotals(
  rows: GroupedRow[]
): AgingFooterTotal[] {
  const byCurrency = new Map<IsoCurrencyCode, AgingFooterTotal>();
  for (const row of rows) {
    let totals = byCurrency.get(row.isoCode);
    if (!totals) {
      totals = {
        isoCode: row.isoCode,
        current: new CurrencyDecimal(0),
        d1_30: new CurrencyDecimal(0),
        d31_60: new CurrencyDecimal(0),
        d61_90: new CurrencyDecimal(0),
        d91: new CurrencyDecimal(0),
        total: new CurrencyDecimal(0),
      };
      byCurrency.set(row.isoCode, totals);
    }
    totals.current = totals.current.plus(row.current);
    totals.d1_30 = totals.d1_30.plus(row.d1_30);
    totals.d31_60 = totals.d31_60.plus(row.d31_60);
    totals.d61_90 = totals.d61_90.plus(row.d61_90);
    totals.d91 = totals.d91.plus(row.d91);
    totals.total = totals.total.plus(sumAgingBuckets(row));
  }
  return Array.from(byCurrency.values());
}
